import React, { useEffect, useState } from 'react';
import { Button, Col, Row, Select, Switch, TimePicker, Form, message } from 'antd';

import './post_automation.css';
import { useTranslation } from 'react-i18next';
import { ApiClient } from '../../api_client';
import { useDispatch, useSelector } from 'react-redux';
import { getUser, getUserId, updateUserMetadata } from '../../store/user.reducer';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { AUTOMATION_TOPICS } from '../../constants';
import { MDBIcon } from 'mdb-react-ui-kit';

dayjs.extend(customParseFormat);
const {Option} = Select;

const PostAutomation = ({automatedMode}: { automatedMode: CallableFunction }) => {
  const [form] = Form.useForm();
  const [pendingRequest, setPendingRequest] = useState(false);
  const [isAutomationEnabled, setIsAutomationEnabled] = useState(false); // New state
  const dispatch = useDispatch();
  const {t} = useTranslation();

  const userId = useSelector(getUserId);
  const user = useSelector(getUser);

  const daysOfWeek = [
    {label: t('automation.days.monday'), value: 1},
    {label: t('automation.days.tuesday'), value: 2},
    {label: t('automation.days.wednesday'), value: 3},
    {label: t('automation.days.thursday'), value: 4},
    {label: t('automation.days.friday'), value: 5},
    {label: t('automation.days.saturday'), value: 6},
    {label: t('automation.days.sunday'), value: 0},
  ];

  const topics: any = AUTOMATION_TOPICS;

  const toggleAutomation = (value: boolean) => {
    setIsAutomationEnabled(value); // Update local state
    automatedMode(value);
    form.setFieldsValue({ automation: value }); // Also update form value
  };

  const handleSubmit = async () => {
    try {
      setPendingRequest(true);
      const values = await form.validateFields();

      await ApiClient.setAutomationParams({
        user: userId,
        topics: values.topics,
        days: values.days,
        time: values.time.format('HH:mm'),
        automation: values.automation
      })

      dispatch(updateUserMetadata({
        automationTopics: values.topics,
        automation: values.automation
      }));

      message.success(t('automation.saveSuccess'));
    } catch (error) {
      console.error('Form validation failed:', error);
      message.error(t('automation.saveError'));
    } finally {
      setPendingRequest(false);
    }
  };

  useEffect(() => {
    if (user?.automation !== undefined) {
      const initialValues = {
        automation: user.automation,
        topics: user.automationTopics,
        days: user.automationDays,
        time: user.automationTime ? dayjs(user.automationTime.toString(), 'HH:mm') : null
      };

      form.setFieldsValue(initialValues);
      setIsAutomationEnabled(user.automation); // Set local state
    }
  }, [user, form]);

  const renderForm = () => {
    return (
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          automation: false,
          days: [],
          time: null
        }}
      >
        <h4 className="mt-4 mb-4">
          {t('automation.title')}
          <Form.Item name="automation" valuePropName="checked" noStyle>
            <Switch
              className="ms-3"
              onChange={toggleAutomation}
              checkedChildren={t('automation.on')}
              unCheckedChildren={t('automation.off')}
            />
          </Form.Item>
        </h4>

        <Row gutter={16} className="mb-3">
          <Col span={12}>
            <Form.Item
              name="topics"
              label={t('automation.mainTheme')}
              rules={[{required: true, message: t('automation.errors.topicRequired')}]}
            >
              <Select
                mode="multiple"
                disabled={!isAutomationEnabled} // Use local state instead
                className="w-100"
                placeholder={t('automation.selectTopic')}
                optionLabelProp="label"
              >
                {topics.sort((a: string, b: string) => a > b).map((topic: string) => (
                  <Option value={topic} key={topic} label={topic}>
                    {t(`topics.${topic}`)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16} className="mb-3">
          <Col span={12}>
            <Form.Item
              name="days"
              label={t('automation.publicationDays')}
              rules={[{required: true, message: t('automation.errors.daysRequired')}]}
            >
              <Select
                mode="multiple"
                disabled={!isAutomationEnabled} // Use local state instead
                className="w-100"
                placeholder={t('automation.selectDays')}
                optionLabelProp="label"
              >
                {daysOfWeek.map(day => (
                  <Option value={day.value} key={day.value} label={day.label}>
                    {day.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="time"
              label={t('automation.publicationTime')}
              rules={[{required: true, message: t('automation.errors.timeRequired')}]}
            >
              <TimePicker
                className="w-100"
                placeholder={t('automation.selectTime')}
                disabled={!isAutomationEnabled} // Use local state instead
                format="HH:mm"
                minuteStep={15}
                showNow={false}
                needConfirm={false}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]} className="mt-4">
          <Col span={24}>
            <Button
              id="generate-post"
              className="btn-with-loader w-100 fw-bold text-center p-2"
              size="large"
              shape="round"
              type="primary"
              loading={pendingRequest ? { icon: <MDBIcon fas icon="circle-notch" spin/> } : false}
              onClick={handleSubmit}
              iconPosition="end"
            >
              {pendingRequest ? t('automation.savePending') : t('automation.save')}
            </Button>
          </Col>
        </Row>
      </Form>
    )
  }

  return renderForm();
};

export default PostAutomation;
